// FULLY DYNAMIC, PAN-INDIA VERSION

const OVERPASS_URL = "https://overpass-api.de/api/interpreter";

/**
 * Find real nearby postal pincodes around a coordinate using OSM data.
 * Returns at most the 4 closest distinct pincodes, sorted nearest-first.
 *
 * @param {number} lat
 * @param {number} lng
 * @param {number} radiusKm - default 3km
 * @returns {Promise<string[]>} up to 4 nearest unique pincodes
 */
export const getNearbyPincodes = async (lat, lng, radiusKm = 3) => {
  if (typeof lat !== "number" || typeof lng !== "number") {
    console.warn("⚠️ getNearbyPincodes: invalid lat/lng", lat, lng);
    return [];
  }

  const MAX_RESULTS = 4;
  const radiusMeters = Math.round(radiusKm * 1000);

  // "out center tags" gives us a lat/lon for ways/relations too (not just
  // nodes), so we can measure real distance and pick the closest ones.
  const query = `
    [out:json][timeout:25];
    nwr["addr:postcode"](around:${radiusMeters},${lat},${lng});
    out center tags qt 300;
  `;

  console.log(
    `\n🚀 getNearbyPincodes called with: lat=${lat}, lng=${lng}, radius=${radiusKm}km`
  );

  try {
    const response = await fetch(OVERPASS_URL, {
      method: "POST",
      headers: { "Content-Type": "text/plain" },
      body: query,
    });

    if (!response.ok) {
      console.warn(`⚠️ Overpass API returned status ${response.status}`);
      return [];
    }

    const data = await response.json();

    // Keep only the closest occurrence of each distinct pincode
    const closestByPincode = new Map();

    (data.elements || []).forEach((el) => {
      const pc = el.tags?.["addr:postcode"];
      if (!pc) return;

      const elLat = el.lat ?? el.center?.lat;
      const elLng = el.lon ?? el.center?.lon;
      if (elLat == null || elLng == null) return;

      const pincode = String(pc).trim();
      const distance = haversineKm(lat, lng, elLat, elLng);

      const existing = closestByPincode.get(pincode);
      if (!existing || distance < existing) {
        closestByPincode.set(pincode, distance);
      }
    });

    const results = Array.from(closestByPincode.entries())
      .sort((a, b) => a[1] - b[1]) // nearest first
      .slice(0, MAX_RESULTS)
      .map(([pincode]) => pincode);

    console.log(`📦 Found ${results.length} nearby pincode(s) (closest ${MAX_RESULTS} max):`, results);
    return results;
  } catch (error) {
    console.warn("⚠️ Overpass fetch failed:", error.message);
    return [];
  }
};

function haversineKm(lat1, lon1, lat2, lon2) {
  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}